import {
  PiiOverrideAudit,
  PiiOverrideConversationSummary,
} from "@/models/pii/PiiOverride";
import { useItemService } from "./useServices";
import { PATH_KEYS } from "@/services/appServices";
import { useQuery } from "@tanstack/react-query";
import { restError } from "@/models/client/RestResponse";

/**
 * PII-override audits for the admin Insights overview (LAR-27 #5). Read-only;
 * the API gates this behind ReadAllUsersConversations. Callers pass the
 * PII-Redaction flag as `enabled` so the queries are inert while it is off.
 */
const usePiiOverride = () => {
  const service = useItemService((services) => services.piiOverride);

  const useQueryByConversation = (
    conversationId: string,
    enabled: boolean = true
  ) =>
    useQuery<PiiOverrideAudit[]>({
      queryKey: [PATH_KEYS.piiOverride, conversationId],
      queryFn: async () => {
        const response = await service.getByConversation(conversationId);
        if (response.success) return response.value;
        throw restError(response);
      },
      enabled: enabled && !!conversationId,
      staleTime: 60_000,
      retry: false,
    });

  /**
   * Batch summaries for one page of the Insights conversations list — a single
   * POST for the visible rows instead of per-row GETs (N+1).
   */
  const useQueryConversationSummaries = (
    conversationIds: string[],
    enabled: boolean = true
  ) =>
    useQuery<PiiOverrideConversationSummary[]>({
      queryKey: [
        PATH_KEYS.piiOverride,
        "summary",
        [...conversationIds].sort().join(","),
      ],
      queryFn: async () => {
        const response = await service.getConversationSummaries(conversationIds);
        if (response.success) return response.value;
        throw restError(response);
      },
      enabled: enabled && conversationIds.length > 0,
      staleTime: 60_000,
      retry: false,
    });

  return { useQueryByConversation, useQueryConversationSummaries };
};

export default usePiiOverride;
